import type {
  ComponentPropsWithoutRef,
  ElementRef,
  FocusEvent,
  PointerEvent,
} from 'react'
import { forwardRef, useRef } from 'react'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import * as PopoverPrimitive from '@radix-ui/react-popover'

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

interface PopoverContentProps extends ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  variant?: 'default' | 'wide' | 'extra-wide'
  padding?: 'default' | 'none' | 'sm' | 'lg'
}

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = 'center',
      sideOffset = 4,
      variant = 'default',
      padding = 'default',
      onPointerDownOutside,
      onInteractOutside,
      ...props
    },
    ref,
  ) => {
    const widthClasses = {
      default: 'w-72',
      wide: 'w-96',
      'extra-wide': 'w-[32rem]',
    }

    const paddingClasses = {
      none: 'p-0',
      sm: 'p-2',
      default: 'p-4',
      lg: 'p-6',
    }

    // Track when the popover OPENS (not mounts) to prevent immediate closing in Tauri
    // The PopoverContent stays mounted while hidden, so we need to detect state transitions
    const openTimeRef = useRef<number>(0)
    const lastStateRef = useRef<string>('closed')
    const observerRef = useRef<MutationObserver | null>(null)
    const isInTauri = isTauri()

    // Store forwarded ref in a ref to avoid dependency issues that cause infinite loops
    // The forwarded ref can change between renders, but we don't want that to recreate callbacks
    const forwardedRefRef = useRef(ref)
    forwardedRefRef.current = ref

    // Combined ref callback
    const combinedRef = (node: HTMLDivElement | null) => {
      // Clean up previous observer
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (node) {
        // Check initial state
        const currentState = node.getAttribute('data-state')
        if (currentState === 'open' && lastStateRef.current !== 'open') {
          openTimeRef.current = Date.now()
        }
        lastStateRef.current = currentState || 'closed'

        // Use MutationObserver to detect state changes
        const observer = new MutationObserver(mutations => {
          for (const mutation of mutations) {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'data-state'
            ) {
              const newState = node.getAttribute('data-state')
              if (newState === 'open' && lastStateRef.current !== 'open') {
                openTimeRef.current = Date.now()
              }
              lastStateRef.current = newState || 'closed'
            }
          }
        })

        observer.observe(node, {
          attributes: true,
          attributeFilter: ['data-state'],
        })
        observerRef.current = observer
      }

      // Forward ref using the ref stored in forwardedRefRef (avoids dependency)
      const forwardedRef = forwardedRefRef.current
      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    }

    // Handle pointer down outside - in Tauri, prevent closing for a short period after OPEN
    const handlePointerDownOutside = (
      e: PointerEvent<HTMLDivElement> | any,
    ) => {
      const timeSinceOpen = Date.now() - openTimeRef.current

      // In Tauri, prevent immediate closing (within 150ms of opening)
      // This fixes a Tauri WebView issue where focus events fire immediately after popover opens
      if (isInTauri && timeSinceOpen < 150) {
        e.preventDefault()
        return
      }

      // Call the original handler if provided
      onPointerDownOutside?.(e)
    }

    // Handle interact outside - same fix for Tauri
    const handleInteractOutside = (e: FocusEvent<HTMLDivElement> | any) => {
      const timeSinceOpen = Date.now() - openTimeRef.current

      // In Tauri, prevent immediate closing (within 150ms of opening)
      if (isInTauri && timeSinceOpen < 150) {
        e.preventDefault()
        return
      }

      // Call the original handler if provided
      onInteractOutside?.(e)
    }

    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={combinedRef}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={16}
          className={cn(
            'rounded-card z-50 origin-[--radix-popover-content-transform-origin] border border-white/20 bg-background/95 text-popover-foreground shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90',
            'data-[state=open]:duration-base data-[state=open]:ease-ios data-[state=closed]:duration-fast data-[state=closed]:ease-ios-in',
            'max-h-[var(--radix-popover-content-available-height)]',
            widthClasses[variant],
            className,
          )}
          onPointerDownOutside={handlePointerDownOutside}
          onInteractOutside={handleInteractOutside}
          {...props}
        >
          {/* Glass gradient overlay */}
          <div className="rounded-card pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 via-white/5 to-transparent dark:from-white/5 dark:via-background/5 dark:to-transparent" />

          {/* Content wrapper - uses flex to properly size scrollable children */}
          <div
            className={cn(
              'relative z-10 flex h-full max-h-[inherit] min-h-0 flex-col',
              paddingClasses[padding],
            )}
          >
            {props.children}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    )
  },
)
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
